import { LibraryFilterParams } from '../models/library/libraryFilterParams.ts';
import { libraryApi } from '../api/libraryApi.ts';
import { CreateLibraryRequest } from '../models/library/createLibraryRequest.ts';
import { UpdateLibraryRequest } from '../models/library/updateLibraryRequest.ts';
import {
    GenerateAiWordbookRequest,
    GenerateAiWordbookResponse,
    GenerateAiGroupLibraryRequest,
    GenerateAiGroupLibraryResponse,
} from '../models/library/aiLibraryRequest.ts';
import {
    useBaseQuery,
    useNoDataMutation,
    useBaseMutation,
    useInvalidateQueries,
} from './useBaseQuery.ts';
import { PaginationData } from '../models/paginationData.ts';
import { CategoryStatsResponse } from '../models/library/categoryStatsResponse.ts';
import { Library, LibraryTier } from '../types/library.ts';
import { LanguageLibraryStatsResponse } from '../models/library/languageLibraryStatsResponse.ts';

// Query Keys
export const libraryKeys = {
    all: ['library'] as const,
    lists: () => [...libraryKeys.all, 'list'] as const,
    list: (filters: LibraryFilterParams) => [...libraryKeys.lists(), filters] as const,
    details: () => [...libraryKeys.all, 'detail'] as const,
    detail: (id: number) => [...libraryKeys.details(), id] as const,
    stats: () => [...libraryKeys.all, 'stats'] as const,
    languageStats: () => [...libraryKeys.all, 'languageStats'] as const,
};

// 목록 조회
export const useLibraryList = (params: LibraryFilterParams) => {
    return useBaseQuery<PaginationData<Library>>(
        libraryKeys.list(params),
        () => libraryApi.getList(params),
        {
            staleTime: 5 * 60 * 1000, // 5분
        }
    );
};

// 단건 조회
export const useLibrary = (id: number) => {
    return useBaseQuery<Library>(
        libraryKeys.detail(id),
        () => libraryApi.getOne(id),
        {
            enabled: !!id,
        }
    );
};

// 생성
export const useCreateLibrary = () => {
    const { invalidate } = useInvalidateQueries();

    return useNoDataMutation<CreateLibraryRequest>(
        (data) => {
            if (data.groupId) {
                return libraryApi.createGroup(data);
            }
            return libraryApi.createWordbook(data);
        },
        {
            onSuccess: () => {
                invalidate(libraryKeys.lists());
            },
        }
    );
};

// 수정
export const useUpdateLibrary = () => {
    const { invalidateMultiple } = useInvalidateQueries();

    return useNoDataMutation<{ id: number; data: UpdateLibraryRequest }>(
        ({ id, data }) => libraryApi.update(id, data),
        {
            onSuccess: (_, variables) => {
                invalidateMultiple([
                    libraryKeys.detail(variables.id),
                    libraryKeys.lists(),
                ]);
            },
        }
    );
};

// 삭제
export const useDeleteLibrary = () => {
    const { invalidate } = useInvalidateQueries();

    return useNoDataMutation<number>(
        (id) => libraryApi.delete(id),
        {
            onSuccess: () => {
                invalidate(libraryKeys.lists());
            },
        }
    );
};

// 노출 토글
export const useToggleVisibility = () => {
    const { invalidateMultiple } = useInvalidateQueries();

    return useNoDataMutation<number>(
        (id) => libraryApi.toggleVisibility(id),
        {
            onSuccess: (_, id) => {
                invalidateMultiple([libraryKeys.detail(id), libraryKeys.lists()]);
            },
        }
    );
};

// 등급 변경 (FREE / PREMIUM)
export const useUpdateTier = () => {
    const { invalidateMultiple } = useInvalidateQueries();

    return useBaseMutation<
        { id: number; tier: LibraryTier },
        { libraryId: number; tier: LibraryTier }
    >(({ id, tier }) => libraryApi.updateTier(id, tier), {
        onSuccess: (_, variables) => {
            invalidateMultiple([
                libraryKeys.detail(variables.id),
                libraryKeys.lists(),
            ]);
        },
    });
};

// 통계 갱신
export const useRefreshStats = () => {
    const { invalidateMultiple } = useInvalidateQueries();

    return useNoDataMutation<number>(
        (id) => libraryApi.refreshStats(id),
        {
            onSuccess: (_, id) => {
                invalidateMultiple([libraryKeys.detail(id), libraryKeys.lists()]);
            },
        }
    );
};

// 카테고리 통계
export const useCategoryStats = (headLang?: string, meaningLang?: string) => {
    return useBaseQuery<CategoryStatsResponse>(
        libraryKeys.stats(),
        () => libraryApi.getCategoryStats(headLang, meaningLang),
        {
            staleTime: 10 * 60 * 1000, // 10분
        }
    );
};

export const useLanguageStats = () => {
    return useBaseQuery<LanguageLibraryStatsResponse>(
        libraryKeys.languageStats(),
        () => libraryApi.getLanguageStats(),
        {
            staleTime: 5 * 60 * 1000, // 5분
        }
    );
};

// AI 단어장 생성
export const useGenerateAiWordbook = () => {
    const { invalidate } = useInvalidateQueries();

    return useBaseMutation<GenerateAiWordbookRequest, GenerateAiWordbookResponse>(
        (data) => libraryApi.generateAiWordbook(data),
        {
            onSuccess: () => {
                invalidate(libraryKeys.lists());
            },
        }
    );
};

// AI 그룹 단어장 생성
export const useGenerateAiGroupLibrary = () => {
    const { invalidate } = useInvalidateQueries();

    return useBaseMutation<
        GenerateAiGroupLibraryRequest,
        GenerateAiGroupLibraryResponse
    >((data) => libraryApi.generateAiGroupLibrary(data), {
        onSuccess: () => {
            invalidate(libraryKeys.lists());
        },
    });
};
